/**
 * `/score/:matchId` — the score confirm + submit surface for the captain
 * SPA (web UI Phase 2 — score editor chunk 2/2). The symmetric sibling
 * of `/lineup/:matchId`.
 *
 * This is the page the iMessage bot links Amy to when it can't
 * confidently parse a score she texted (`src/bot/dispatcher.ts` →
 * `${WEB_APP_BASE}/score/${resolvedMatchId}`). The bot link carries the
 * MATCH id ONLY — never pre-parsed games (the bot only ever sends a
 * score link when `parseScoreText` FAILED, so there are no trustworthy
 * games to prefill). The grid therefore starts empty and Amy fills the
 * 4 lines herself.
 *
 * AGENTS.md RULE 3 — scores are NEVER auto-submitted. The only valid
 * flow is the two-phase confirm, done here in the browser:
 *
 *   1. Captain fills 4 lines → "Review" → report_score(dry_run=true)
 *      returns the built payload + a confirmation_token. We render the
 *      "this is what will be submitted to WTTA" panel.
 *   2. "Submit to WTTA" (enabled ONLY after a successful Review, and
 *      INVALIDATED the moment any line is edited — re-Review required)
 *      → report_score(dry_run=false, confirmation_token). The server
 *      re-derives the token from the submitted lines and refuses
 *      (CONFIRMATION_MISMATCH) if they changed.
 *
 * There is NO code path that calls report_score with dry_run=false
 * without (a) a prior successful dry_run and (b) the matching
 * confirmation_token from that dry_run held in component state. Editing
 * any line clears that token, which both disables the Submit button and
 * — even if that were bypassed — would fail the server's token check.
 *
 * Honest WTTA refusal: `submitToWtta` currently returns typed refusals
 * (`WTTA_CRED_MISSING` / `WTTA_SUBMIT_NOT_WIRED`) because Amy's WTTA
 * session credential + the real form schema aren't available yet. The
 * dry-run already validated the score and the payload is correct, so we
 * surface a DISTINCT "validated & recorded, live submit not wired yet"
 * state — NOT a fake success, and visibly different from a real one.
 *
 * Data access is exclusively via the HTTP tool gateway through
 * `lib/score-api.ts`. The browser has only the session cookie; it never
 * touches Supabase or the MCP server directly. Match context (opponent
 * / date) for the header is a best-effort read of the same
 * `GET /api/team/overview` the Season page uses — purely cosmetic; the
 * editor works fully even when the match isn't in the 14-day window.
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { apiGet } from "../lib/api";
import {
  reportScoreDryRun,
  reportScoreSubmit,
  ScoreToolError,
  type ScoreDryRun,
  type ScoreLineInput,
  type ScoreSubmit,
} from "../lib/score-api";
import { useAuthedSession } from "../components/RequireAuth";

type LineNum = 1 | 2 | 3 | 4;
const LINES: readonly LineNum[] = [1, 2, 3, 4] as const;
const MAX_GAMES = 99;

/**
 * One editable row. Games are kept as STRINGS so an empty input isn't
 * coerced to 0 (which would silently pass the no-tie check at 0-0). We
 * parse + validate on Review.
 */
type EditableLine = {
  line_number: LineNum;
  home_games: string;
  away_games: string;
};

function emptyLines(): EditableLine[] {
  return LINES.map((n) => ({
    line_number: n,
    home_games: "",
    away_games: "",
  }));
}

type MatchHeader = { opponent: string | null; date: string | null };

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; header: MatchHeader | null }
  | { kind: "error"; message: string };

// ─── Error-code → captain copy ─────────────────────────────────────────
//
// The gateway scrubs the real MCP message and preserves only the `code`
// (server-side codes are emitted as a `BAD_INPUT`-prefixed message which
// the gateway collapses to the code `BAD_INPUT`; the prefix word is
// gone, so we re-attach human copy by code here). Unknown codes fall
// back to a line that still names the code so a captain can screenshot.

function explainError(e: InstanceType<typeof ScoreToolError>): string {
  switch (e.code) {
    case "CONFIRMATION_MISMATCH":
      return "The score changed since you reviewed it. Tap Review again to re-check the new score, then submit.";
    case "CONFIRMATION_REQUIRED":
      return "Review the score first, then submit.";
    case "DRY_RUN_REQUIRED":
      return "Something went wrong with the review step. Tap Review again.";
    case "BAD_INPUT":
      // report_score folds LINE_COUNT / LINE_TIE / GAMES_RANGE /
      // MATCH_NOT_FOUND into BAD_INPUT once the gateway scrubs it.
      return "The score wasn't accepted. Each of the 4 lines needs whole game counts (0–99) and no line can be tied (home can't equal away). Fix the lines and tap Review.";
    case "MATCH_NOT_FOUND":
      return "That match wasn't found. The link may be stale — re-check with the bot.";
    case "RATE_LIMITED":
      return "Too many requests. Wait a minute, then retry.";
    case "UNAUTHENTICATED":
      return "Your session expired. Reload the page to sign in again.";
    case "NOT_FOUND":
      return "You don't have permission to report a score on this team.";
    case "UPSTREAM_DOWN":
      return "The scoring service is unreachable right now. Try again in a minute.";
    default:
      return `The server reported an error (${e.code}). Try again, or re-check with the bot.`;
  }
}

/**
 * Is this tool error the KNOWN-BLOCKED honest WTTA refusal (cred missing
 * / form not wired)? The gateway scrubs the message to a generic string
 * and collapses the code to `BAD_INPUT` (report_score returns these via
 * `badInput(...)`), so we can't distinguish it from a real validation
 * BAD_INPUT by code alone. But this branch is ONLY reachable from the
 * submit (dry_run=false) call AFTER a successful Review already proved
 * the lines valid — so a BAD_INPUT on submit, post-Review, is the WTTA
 * refusal, not bad lines. The caller passes `afterReview` to scope this.
 */
function isWttaNotWired(
  e: InstanceType<typeof ScoreToolError>,
  afterReview: boolean,
): boolean {
  return afterReview && e.httpStatus === 200 && e.code === "BAD_INPUT";
}

// ─── Client-side validation (mirrors validateScoreLines server-side) ───
//
// The SERVER is authoritative; this just gives immediate feedback so a
// captain isn't round-tripping the gateway to learn she left a line
// blank. Same rules as `src/lib/wtta-score.ts#validateScoreLines`.

function validateClient(lines: EditableLine[]): {
  parsed: ScoreLineInput[] | null;
  error: string | null;
} {
  const parsed: ScoreLineInput[] = [];
  for (const l of lines) {
    for (const [label, raw] of [
      ["Home", l.home_games],
      ["Away", l.away_games],
    ] as const) {
      if (raw.trim() === "") {
        return {
          parsed: null,
          error: `Line ${l.line_number}: enter ${label.toLowerCase()} games.`,
        };
      }
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 0 || n > MAX_GAMES) {
        return {
          parsed: null,
          error: `Line ${l.line_number} ${label.toLowerCase()} games must be a whole number 0–${MAX_GAMES}.`,
        };
      }
    }
    const home = Number(l.home_games);
    const away = Number(l.away_games);
    if (home === away) {
      return {
        parsed: null,
        error: `Line ${l.line_number} is tied (${home}-${away}). A WTTA line can't tie — one side wins on games.`,
      };
    }
    parsed.push({
      line_number: l.line_number,
      home_games: home,
      away_games: away,
    });
  }
  return { parsed, error: null };
}

function verdictText(r: ScoreDryRun): string {
  const { score_result, payload } = r;
  const tally = `${payload.home_lines_won}-${payload.away_lines_won}`;
  if (score_result === "home_win") return `WIN ${tally}`;
  if (score_result === "away_win") return `LOSS ${tally}`;
  return `TIE ${tally}`;
}

// ─── Match-header best-effort lookup ───────────────────────────────────
//
// `report_score` returns no opponent/date and `get_lineup_card` doesn't
// either; the lightest EXISTING path with both is the team overview the
// Season page already calls. Best-effort + non-fatal: if the match
// isn't in the 14-day upcoming window (a just-finished match often
// isn't) we render a generic header — the editor is fully functional
// without it. No Supabase browser path is added.

type OverviewLite = {
  upcoming_matches_14d?: { match_id: string; date: string; opponent: string | null }[];
};

async function loadHeader(matchId: string): Promise<MatchHeader | null> {
  try {
    const data = await apiGet<OverviewLite>("/api/team/overview");
    const m = (data.upcoming_matches_14d ?? []).find(
      (x) => x.match_id === matchId,
    );
    if (!m) return null;
    return { opponent: m.opponent ?? null, date: m.date ?? null };
  } catch {
    return null; // cosmetic only — never block the editor on this
  }
}

function formatMatchDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ─── Page ──────────────────────────────────────────────────────────────

export default function ScoreEditor() {
  // Behind RequireAuth; calling this asserts a live session.
  useAuthedSession();
  const { matchId } = useParams<{ matchId: string }>();

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [lines, setLines] = useState<EditableLine[]>(emptyLines());
  const [busy, setBusy] = useState<null | "review" | "submit">(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // The reviewed dry-run + the EXACT lines it was computed from. Editing
  // any line clears `review` (so Submit disables + re-Review is forced) —
  // this is the rule-3 "edited after Review invalidates the token" gate,
  // mirroring the lineup editor's switch-discards-edits pattern.
  const [review, setReview] = useState<ScoreDryRun | null>(null);
  const [submitted, setSubmitted] = useState<ScoreSubmit | null>(null);
  const [wttaBlocked, setWttaBlocked] = useState(false);

  useEffect(() => {
    if (!matchId) {
      setState({
        kind: "error",
        message: "No match in the link. Re-check with the bot.",
      });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      const header = await loadHeader(matchId);
      if (cancelled) return;
      setState({ kind: "ready", header });
    })();
    return () => {
      cancelled = true;
    };
  }, [matchId]);

  const clientCheck = useMemo(() => validateClient(lines), [lines]);

  function setGames(
    line_number: LineNum,
    slot: "home_games" | "away_games",
    value: string,
  ) {
    // Keep only digits (still a string so empty stays empty).
    const cleaned = value.replace(/[^0-9]/g, "").slice(0, 2);
    setLines((prev) =>
      prev.map((l) =>
        l.line_number === line_number ? { ...l, [slot]: cleaned } : l,
      ),
    );
    // Any edit invalidates a prior Review (rule 3) + clears errors.
    setReview(null);
    setActionError(null);
  }

  async function onReview() {
    if (!matchId) return;
    if (!clientCheck.parsed) {
      setActionError(clientCheck.error);
      return;
    }
    setBusy("review");
    setActionError(null);
    setReview(null);
    try {
      const r = await reportScoreDryRun(matchId, clientCheck.parsed);
      setReview(r);
    } catch (e) {
      setActionError(
        e instanceof ScoreToolError
          ? explainError(e)
          : "Couldn't review the score. Retry.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function onSubmit() {
    if (!matchId || !review || !clientCheck.parsed) return;
    setBusy("submit");
    setActionError(null);
    setWttaBlocked(false);
    try {
      const r = await reportScoreSubmit(
        matchId,
        clientCheck.parsed,
        review.confirmation_token,
      );
      setSubmitted(r);
    } catch (e) {
      if (e instanceof ScoreToolError && isWttaNotWired(e, true)) {
        // Honest blocked dependency — the score IS validated (the
        // prior Review proved the lines + the payload is correct) but
        // the live WTTA POST isn't wired yet. NOT a fake success.
        setWttaBlocked(true);
      } else {
        setActionError(
          e instanceof ScoreToolError
            ? explainError(e)
            : "Submit failed. Retry.",
        );
      }
    } finally {
      setBusy(null);
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────

  if (state.kind === "loading") {
    return (
      <section>
        <p className="muted">Loading score…</p>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section>
        <h1>Report score</h1>
        <p role="alert" style={{ color: "#b3261e" }}>
          {state.message}
        </p>
      </section>
    );
  }

  const header = state.header;

  // ─── Terminal: real WTTA submission succeeded ────────────────────────
  if (submitted) {
    const idempotent = submitted.already_submitted === true;
    return (
      <section>
        <h1>Score submitted ✅</h1>
        <p>
          {idempotent
            ? "This score was already submitted — no change."
            : "Reported to WTTA."}
          {submitted.wtta_confirmation && (
            <>
              {" "}
              Confirmation{" "}
              <code>{submitted.wtta_confirmation}</code>
            </>
          )}
        </p>
        <p className="muted" style={{ marginTop: "1rem" }}>
          <small>
            You're done. Text the bot if WTTA shows something different
            and it needs a correction.
          </small>
        </p>
      </section>
    );
  }

  // ─── Terminal: validated + recorded, live WTTA submit not wired ──────
  // Deliberately styled DIFFERENTLY from the green success above — same
  // "we did our part" honesty, but it is NOT submitted to WTTA yet.
  if (wttaBlocked && review) {
    return (
      <section>
        <h1>Score validated — WTTA submission pending ⏳</h1>
        <div
          style={{
            border: "1px solid #e0b400",
            background: "#fffbe6",
            borderRadius: 6,
            padding: "0.75rem 1rem",
            marginTop: "0.5rem",
          }}
        >
          <p style={{ margin: 0 }}>
            <strong>{verdictText(review)}</strong> —{" "}
            {review.lines_summary}
          </p>
          <p style={{ margin: "0.6rem 0 0" }}>
            Your score passed validation and the submission payload is
            correct and recorded. <strong>It has not been sent to
            WTTA yet</strong> — live WTTA submission isn't wired up
            (it's blocked on the captain's WTTA login credential, a
            known v0.1 dependency).
          </p>
          <p className="muted" style={{ margin: "0.6rem 0 0" }}>
            <small>
              Nothing more to do here for now. Enter this score on
              wttatennis.com manually until the integration lands; the
              bot will confirm when it does.
            </small>
          </p>
        </div>
      </section>
    );
  }

  // ─── Editor ──────────────────────────────────────────────────────────
  const canReview = clientCheck.parsed !== null && busy === null;
  const canSubmit = review !== null && busy === null;

  return (
    <section>
      <h1>Report score</h1>
      <p className="muted" style={{ marginTop: "0.25rem" }}>
        <small>
          {header?.opponent ? <>vs {header.opponent} · </> : null}
          {header?.date ? formatMatchDate(header.date) : "This match"} —
          enter games won per line for all 4 lines, Review, then submit.
        </small>
      </p>

      <div style={{ overflowX: "auto" }}>
        <table className="stacked-fields" style={tableStyle}>
          <thead>
            <tr style={theadRow}>
              <th style={thLead}>Line</th>
              <th style={th}>Our games</th>
              <th style={th}>Their games</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.line_number} style={tbodyRow}>
                <td data-label="Line" style={tdLead}>
                  {l.line_number}
                </td>
                <td data-label="Our games" style={td}>
                  <GamesInput
                    label={`Line ${l.line_number} our games`}
                    value={l.home_games}
                    disabled={busy !== null}
                    onChange={(v) =>
                      setGames(l.line_number, "home_games", v)
                    }
                  />
                </td>
                <td data-label="Their games" style={td}>
                  <GamesInput
                    label={`Line ${l.line_number} their games`}
                    value={l.away_games}
                    disabled={busy !== null}
                    onChange={(v) =>
                      setGames(l.line_number, "away_games", v)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Live client-side hint (server is still authoritative). */}
      {clientCheck.error && (
        <p className="muted" style={{ marginTop: "0.5rem" }}>
          <small>{clientCheck.error}</small>
        </p>
      )}

      {actionError && (
        <p role="alert" style={{ color: "#b3261e" }}>
          {actionError}
        </p>
      )}

      {/* Review confirmation panel — "this is what gets submitted". */}
      {review && (
        <div
          style={{
            border: "2px solid #1a1a1a",
            borderRadius: 6,
            padding: "0.75rem 1rem",
            margin: "1rem 0",
            background: "#fff",
          }}
        >
          <p style={{ margin: 0 }}>
            <strong>Review — this is what will be submitted to WTTA</strong>
          </p>
          <p style={{ margin: "0.5rem 0 0", fontSize: "1.05rem" }}>
            <strong>{verdictText(review)}</strong>
          </p>
          <ol
            style={{
              margin: "0.5rem 0 0",
              paddingLeft: "1.25rem",
              fontSize: "0.95rem",
            }}
          >
            {review.payload.lines
              .slice()
              .sort((a, b) => a.line_number - b.line_number)
              .map((l) => (
                <li key={l.line_number}>
                  Line {l.line_number}: {l.home_games}-{l.away_games}{" "}
                  <span className="muted">
                    ({l.winner === "home" ? "we win" : "they win"})
                  </span>
                </li>
              ))}
          </ol>
          <p className="muted" style={{ margin: "0.5rem 0 0" }}>
            <small>
              Summary: {review.lines_summary}. Edit any line above to
              re-review before submitting.
            </small>
          </p>
        </div>
      )}

      <p style={{ marginTop: "1rem" }}>
        <button
          type="button"
          className="button"
          onClick={onReview}
          disabled={!canReview}
        >
          {busy === "review" ? "Reviewing…" : "Review"}
        </button>{" "}
        <button
          type="button"
          className="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          title={
            review
              ? "Submit the reviewed score to WTTA"
              : "Review the score first"
          }
        >
          {busy === "submit" ? "Submitting…" : "Submit to WTTA"}
        </button>
      </p>
      <p className="muted">
        <small>
          Nothing is submitted until you Review and then Submit. Editing
          a line after Review clears it — you'll re-Review the changed
          score first (the score you confirm is the score that's sent).
        </small>
      </p>
    </section>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function GamesInput({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      aria-label={label}
      value={value}
      disabled={disabled}
      placeholder="0"
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        minWidth: 64,
        maxWidth: 96,
        padding: "0.4rem",
        fontFamily: "inherit",
        fontSize: "1rem",
        textAlign: "center",
      }}
    />
  );
}

// ─── Shared inline styles (match LineupEditor.tsx / Season.tsx) ────────

const tableStyle: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: "0.5rem",
};
const theadRow: CSSProperties = {
  borderBottom: "1px solid #e5e5e5",
  textAlign: "left",
};
const tbodyRow: CSSProperties = {
  borderBottom: "1px solid #f0f0f0",
};
const th: CSSProperties = { padding: "0.5rem" };
const thLead: CSSProperties = {
  padding: "0.5rem 0.5rem 0.5rem 0",
};
const td: CSSProperties = { padding: "0.5rem" };
const tdLead: CSSProperties = {
  padding: "0.5rem 0.5rem 0.5rem 0",
};
